import { useApolloClient } from '@apollo/client';
import { Box, FieldSet, Gleam, Label, LabeledControl, Legend, Radio, VisuallyHidden } from '@zillow/constellation-10';
import { useContext } from 'react';
import {
  ConversationList_GetConversationsDocument,
  ReadRepliedStatus,
  useStatusSelector_GetBadgeCountsQuery,
} from '../../../../types/GraphqlTypes';
import { MAX_OLD_CONVERSATION_DAYS } from '../../../../constants/ConfigConstants';
import useInboxRouterHelper from '../../../../hooks/useInboxRouterHelper';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import { logApolloError } from '../../../../utils/errorUtils';

export default function StatusSelector() {
  const apolloClient = useApolloClient();
  const { changeFiltersOrFolders } = useInboxRouterHelper();
  const { clientTimeZone, daysBack, folder, listingAlias, isByProperty, readRepliedStatus, renterProgressStatus } =
    useContext(InboxPageContext);
  const { data: badgeCountData, error } = useStatusSelector_GetBadgeCountsQuery({
    fetchPolicy: 'network-only',
  });
  const unreadConversationCount = badgeCountData?.ZRM_getAggregateConversationCount?.unreadCount || 0;
  const shouldShowUnreadCount = !isByProperty && unreadConversationCount > 0;

  const handleStatusChange = async (selectedStatus: ReadRepliedStatus) => {
    await apolloClient.query({
      query: ConversationList_GetConversationsDocument,
      variables: {
        listingAlias: listingAlias || null,
        clientTimeZone,
        daysBack: daysBack ? MAX_OLD_CONVERSATION_DAYS : null,
        folder,
        renterProgressStatus,
        readRepliedStatus: selectedStatus,
      },
      fetchPolicy: 'network-only',
    });

    await changeFiltersOrFolders({
      folder,
      readRepliedStatus: selectedStatus,
      renterProgressStatus,
      byProperty: isByProperty,
      listingAlias: listingAlias,
    });
  };

  const getOnChangeHandler = (selectedStatus: ReadRepliedStatus) => () => handleStatusChange(selectedStatus);

  if (error) {
    logApolloError(error, {
      details: 'FiltersModule_StatusSelector - Error getting badge counts for filters module',
      listingAlias,
      readRepliedStatus,
    });
  }

  return (
    <FieldSet
      legend={
        <VisuallyHidden>
          <Legend>Filter by conversation status</Legend>
        </VisuallyHidden>
      }
      css={{
        paddingLeft: 'tighter',
        paddingBottom: 'default',
      }}
    >
      <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <LabeledControl
          label={<Label>Show unread only</Label>}
          control={
            <Radio
              name="status-filter-option"
              checked={readRepliedStatus === ReadRepliedStatus.Unread}
              onChange={getOnChangeHandler(ReadRepliedStatus.Unread)}
            />
          }
        />
        {shouldShowUnreadCount && (
          <Box>
            <Gleam appearance="ellipsis" count={unreadConversationCount} maxCount={99} tone="default" />
          </Box>
        )}
      </Box>

      <LabeledControl
        label={<Label>Not replied to</Label>}
        control={
          <Radio
            name="status-filter-option"
            checked={readRepliedStatus === ReadRepliedStatus.NotRepliedTo}
            onChange={getOnChangeHandler(ReadRepliedStatus.NotRepliedTo)}
          />
        }
      />
    </FieldSet>
  );
}
